import React from "react";
import { MedicationSearchResult } from "model/medication-search-result.model";
import { MedicationTypeEnum } from "common/enums/MedicationTypeEnum";
import { Col, Row } from "@cc/react-components";

export interface Props {
  medicationSearchResult: MedicationSearchResult;
}

const MedicationSearchResultRow = ({ medicationSearchResult }: Props) => {
  return (
    <Row className="align-items-center no-gutters">
      <Col xs={7}>{medicationSearchResult.name}</Col>
      <Col xs={1}>
        {getMedicationTypeIcon(
          medicationSearchResult.type,
          medicationSearchResult.otc
        )}
      </Col>
      <Col xs={1}>
        {getCheapCategoryIcon(
          medicationSearchResult.type,
          medicationSearchResult.cheapCategoryInfo
        )}
      </Col>
      <Col xs={1}>
        {getReimbursementCodeIcons(medicationSearchResult.reimbursementCodes)}
      </Col>
      <Col xs={2}>
        <div className="text-right text-nowrap align-middle">
          {medicationSearchResult.price > 0 &&
            "€ " + medicationSearchResult.price}
        </div>
      </Col>
    </Row>
  );
};

const getMedicationTypeIcon = (type?: MedicationTypeEnum, otc?: boolean) => {
  switch (type) {
    case MedicationTypeEnum.FREETEXT:
      if (otc) {
        return renderMedicationTypeIcon("otc", 20, 13);
      } else {
        return renderMedicationTypeIcon("othermedication", 16, 16);
      }
    case MedicationTypeEnum.MAGISTRAL:
      return renderMedicationTypeIcon("magistral", 17, 16);
    case MedicationTypeEnum.MEDICINAL_PRODUCT:
      if (otc) {
        return renderMedicationTypeIcon("otc", 20, 13);
      } else {
        return renderMedicationTypeIcon("package", 13, 20);
      }
    case MedicationTypeEnum.SUBSTANCE_PRODUCT:
    default:
      return renderMedicationTypeIcon("vos", 20, 13);
  }
};

const renderMedicationTypeIcon = (
  fileName: string,
  width: number,
  height: number
) => {
  return (
    <div>
      <img
        src={`/images/${fileName}.png`}
        width={width + "px"}
        height={height + "px"}
        alt={fileName}
      />
    </div>
  );
};

const getCheapCategoryIcon = (type: MedicationTypeEnum, category?: string) => {
  if (type !== MedicationTypeEnum.MEDICINAL_PRODUCT) {
    return null;
  }
  switch (category) {
    case "C": // Copy, without supplement of the co-payment and in the cheap category
    case "G": // Generic medicine without supplement of the co-payment and in the cheap category
      return <img src={"/images/bcfi_greencircle.png"} alt="cheap" />;
    case "B": // Original medicine without supplement of the co-payment, but not in the cheap category
    case "Y": // Original medicine without supplement of the co-payment, but not in the cheap category
    case "W": // Orphan medicine without supplement of the co-payment, but not in the cheap category
    case "Z": // Original medicine without supplement of the co-payment for which ATC4 alternatives exist, but not in the cheap category
      return <img src={"/images/bcfi_bluecircle.png"} alt="not cheap" />;
    case "A":
    case "R":
      return (
        <img
          src={"/images/bcfi_orangecircle.png"}
          alt="not cheap and reimbursement"
        />
      );
    default:
      return null;
  }
};

const getReimbursementCodeIcons = (codes: Array<string>) => {
  return codes.map((code: string, index: number) => {
    const iconList = [];
    for (const char of code) {
      iconList.push(getIcon(char, code, iconList.length));
      if (char === "c") {
        break;
      }
    }
    return <div key={index}>{iconList}</div>;
  });
};

const getIcon = (char: string, fullCode: string, index: number) => {
  switch (char) {
    case "a": // Full refund
      return renderTextIcon("a", index);
    case "b": // Majority refund with maximum co-payment
      return renderTextIcon("b", index);
    case "h": // Refund when used in hospital
      return renderTextIcon("h", index);
    case "j": // Refund for contraception in women younger than 21y
      return renderTextIcon("J", index);
    case "c":
      if (fullCode === "cs") {
        // Partial refund with co-payment 60% without maximum
        return renderTextIcon("cs", index);
      } else if (fullCode === "cx") {
        // Partial refund with co-payment 80% without maximum
        return renderTextIcon("cx", index);
      } else if (fullCode === "chr") {
        // Refund when in chronic pain
        return renderTextIcon("chr", index);
      } else {
        // Partial refund with maximum co-payment
        return renderTextIcon("c", index);
      }
    case "2": // A posteriori
      //TODO
      // return <Icon key={index} type="bcfi_after" variant="h4" />;
      return renderTextIcon("B_A", index);
    case "4": // A priori
      //TODO decoment
      // return <Icon key={index} type="bcfi_before" variant="h4" />;
      return renderTextIcon("B_B", index);
    case "d": // No refund
    default:
      return null;
  }
};

const renderTextIcon = (text: string, index: number) => {
  return (
    <div key={index} className="text-center font-weight-bold align-self-center">
      {text}
    </div>
  );
};

export default MedicationSearchResultRow;
